import { useState } from 'react';
import { X, Loader2, Zap } from 'lucide-react';
import api from '../api/axios';
import Swal from 'sweetalert2';

const PLANS = {
  growth: { label: '+30 Interviews', interviews: 30, total: '₹4,500', per: '₹150 / interview' },
  scale:  { label: '+60 Interviews', interviews: 60, total: '₹7,200', per: '₹120 / interview' },
};

const UpgradeModal = ({ plan, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const config = PLANS[plan];
  if (!config) return null;

  const handlePay = async () => {
    setLoading(true);
    try {
      // 1. Create Razorpay order on backend
      const { data } = await api.post(`/payments/create-order?plan=${plan}`);

      // 2. Open Razorpay checkout
      const options = {
        key:         process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount:      data.amount,
        currency:    data.currency,
        name:        'RecruitGuru',
        description: `${config.label} – ${config.per}`,
        order_id:    data.order_id,
        prefill: {
          name:    '',
          email:   '',
          contact: '',
        },
        handler: async (response) => {
          try {
            // 3. Verify on backend — this tops up interviews_remaining
            await api.post('/payments/verify', {
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              plan,
            });
            onSuccess?.();
          } catch (err) {
            Swal.fire({
              icon: 'error',
              title: 'Payment verification failed',
              text: 'Please contact support with your payment ID: ' + response.razorpay_payment_id
            });
          }
        },
        modal: {
          ondismiss: () => setLoading(false),
        },
        theme: { color: '#4F46E5' },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      console.error(err);
      // alert('Could not initiate payment. Please try again.');
      Swal.fire({
        icon: 'error',
        title: 'Could not initiate payment',
        text: 'Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-[#111] border border-slate-200 dark:border-white/10 rounded-[2rem] p-10 w-full max-w-md shadow-2xl animate-in zoom-in-95 duration-200">

        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 flex items-center justify-center">
            <Zap size={18} className="text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="text-[12px] font-bold tracking-wide text-indigo-600 dark:text-indigo-400">
            Top Up Interviews
          </div>
        </div>

        <div className="flex items-end gap-2 mt-4 mb-1">
          <span className="text-5xl font-bold text-slate-900 dark:text-white">{config.total}</span>
        </div>
        <p className="text-[12px] text-slate-400 tracking-wide font-bold mb-8">
          {config.per}
        </p>

        {/* Interview count highlight */}
        <div className="bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded-2xl p-5 mb-8 text-center">
          <span className="text-4xl font-bold text-indigo-700 dark:text-indigo-300">{config.interviews}</span>
          <p className="text-xs text-indigo-500 dark:text-indigo-400 tracking-wide font-bold mt-1">
            interviews added to your balance
          </p>
        </div>

        <button
          onClick={handlePay}
          disabled={loading}
          className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-xs font-bold tracking-wide transition-all shadow-[0_0_20px_rgba(99,102,241,0.3)] flex items-center justify-center gap-2"
        >
          {loading
            ? <><Loader2 size={16} className="animate-spin" /> Opening checkout...</>
            : `Pay ${config.total}`
          }
        </button>

        <p className="text-[12px] text-center text-slate-400 mt-4">
          One-time purchase · Interviews never expire · GST applicable
        </p>
      </div>
    </div>
  );
};

export default UpgradeModal;